'use client';

import { QueryClientContext } from '@tanstack/react-query';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';

import { getSharedScopedSupabaseClient, sanitizeChannelName } from '@/lib/realtime';
import { markRealtimeSync, subscribeRealtimeHealth } from '@/lib/realtime-health';

/**
 * "This menu changed" for guests (blueprint §4.4).
 *
 * Replaces seven postgres_changes subscriptions per guest with broadcast topics
 * fired by database triggers (20261121000000_menu_change_broadcast.sql):
 *   menu:<menuId>, branch-menu:<branchId>, template:<templateId>.
 * Payloads carry no row data, so nothing internal can leak; on a signal we just
 * refetch through the normal public API. Public topics need no auth, so every
 * guest shares the anon client and one socket.
 *
 * Safety nets, because a missed signal must never leave a guest ordering a dish
 * that was taken off: refetch when the tab becomes visible again, and when the
 * realtime health monitor says the connection died.
 */
export function useMenuChangeSignal(
  topics: { menuId?: string | null; branchId?: string | null; templateId?: string | null },
  onChange: () => void,
  queryKeys: unknown[][] = [],
) {
  const queryClient = useContext(QueryClientContext);
  const onChangeRef = useRef(onChange);
  const keysRef = useRef(queryKeys);
  onChangeRef.current = onChange;
  keysRef.current = queryKeys;
  const [epoch, setEpoch] = useState(0);
  const aliveRef = useRef(true);

  const names = useMemo(
    () =>
      [
        topics.menuId ? `menu:${topics.menuId}` : null,
        topics.branchId ? `branch-menu:${topics.branchId}` : null,
        topics.templateId ? `template:${topics.templateId}` : null,
      ].filter((t): t is string => Boolean(t)),
    [topics.menuId, topics.branchId, topics.templateId],
  );
  const signature = names.join('|');

  useEffect(() => {
    const fire = () => {
      keysRef.current.forEach((queryKey) => queryClient?.invalidateQueries({ queryKey: queryKey as unknown[] }));
      onChangeRef.current();
    };
    const offHealth = subscribeRealtimeHealth((snap) => {
      if (aliveRef.current && !snap.alive) {
        fire();
        setEpoch((e) => e + 1);
      }
      aliveRef.current = snap.alive;
    });
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') hiddenAt = Date.now();
      // Back after more than a minute away: the menu may have changed while sockets slept.
      else if (hiddenAt && Date.now() - hiddenAt > 60_000) fire();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      offHealth();
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [queryClient]);

  useEffect(() => {
    if (!names.length) return;
    const client = getSharedScopedSupabaseClient(null);
    if (!client) return;
    let timer: number | null = null;
    const channels: RealtimeChannel[] = names.map((topic) =>
      client
        // The broadcast topic IS the channel name; sanitize only guards odd ids.
        .channel(sanitizeChannelName(topic))
        .on('broadcast', { event: 'changed' }, () => {
          markRealtimeSync();
          if (timer !== null) return;
          // Coalesce bursts without postponing availability indefinitely during a bulk edit.
          timer = window.setTimeout(() => {
            timer = null;
            keysRef.current.forEach((queryKey) => queryClient?.invalidateQueries({ queryKey: queryKey as unknown[] }));
            onChangeRef.current();
          }, 150);
        })
        .subscribe(),
    );
    return () => {
      if (timer) window.clearTimeout(timer);
      channels.forEach((channel) => void channel.unsubscribe());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `signature` is `names` as a stable string
  }, [signature, epoch, queryClient]);
}
